import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

const BackgroundAudioPlayer = () => {
  const audioContextRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const { url, isPlaying } = useSelector((state) => state.audio);
  const wakeLockRef = useRef(null);

  // Initialize audio context and request wake lock
  useEffect(() => {
    const initializeAudio = async () => {
      try {
        // Create audio context
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        
        // Request wake lock to prevent device from sleeping
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        }
      } catch (error) {
        console.error('Error initializing audio:', error);
      }
    };

    initializeAudio();

    // Cleanup function
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
      }
    };
  }, []);

  // Handle audio playback
  useEffect(() => {
    const playAudio = async () => {
      if (!audioContextRef.current || !url) return;

      try {
        // Resume audio context if it's suspended
        if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
        }

        // Create new audio source
        const response = await fetch(url);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
        
        // Create and configure source node
        sourceNodeRef.current = audioContextRef.current.createBufferSource();
        sourceNodeRef.current.buffer = audioBuffer;
        sourceNodeRef.current.connect(audioContextRef.current.destination);
        
        // Start playback if isPlaying is true
        if (isPlaying) {
          sourceNodeRef.current.start(0);
        }
      } catch (error) {
        console.error('Error playing audio:', error);
      }
    };

    playAudio();

    // Cleanup function
    return () => {
      if (sourceNodeRef.current) {
        sourceNodeRef.current.stop();
        sourceNodeRef.current.disconnect();
      }
    };
  }, [url]);

  // Handle play/pause
  useEffect(() => {
    if (!audioContextRef.current) return;

    if (isPlaying) {
      audioContextRef.current.resume();
    } else {
      audioContextRef.current.suspend();
    }
  }, [isPlaying]);

  return null; // This component doesn't render anything
};

export default BackgroundAudioPlayer; 